import {
  Box,
  Button,
  Checkbox,
  createStyles,
  Divider,
  Flex,
  Grid,
  Group,
  Input,
  LoadingOverlay,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { map, uniq } from 'lodash/fp';
import React from 'react';
import { useHistory } from 'react-router-dom';

import {
  DeviceModelType,
  useCreateDeviceModel,
  UseCreateDeviceModelParams,
  useDeviceModelApiVersions,
  useEditDeviceModel,
} from '@portals/api/partners';
import { IconPicker } from '@portals/core';
import { DEVICE_MODEL_ICONS, ModalProps } from '@portals/framework';
import { ReactComponent as QuestionCircle } from '@portals/icons/linear/question-circle.svg';
import {
  AuthMethodType,
  CommunicationProtocolType,
  DeviceModelIconName,
} from '@portals/types';

export interface ConfigureModelProps
  extends ModalProps<{
    isCreate: boolean;
    model: DeviceModelType | undefined;
    onSuccess?: (deviceModel: DeviceModelType) => void;
  }> {}

const DEFAULT_TTL_THRESHOLD = 15;

export function ConfigureModel({ closeMe, data }: ConfigureModelProps) {
  const { isCreate, model, onSuccess } = data;

  const history = useHistory();
  const switchStyles = useSwitchStyles();

  const form = useForm<Partial<UseCreateDeviceModelParams>>({
    initialValues: {
      name: model?.model || '',
      sku: model?.sku ?? '',
      create_reboot_command: true,
      create_firmware_upgrade_command: true,
      create_dump_command: true,
      auth_method: model?.auth_method || AuthMethodType.CloudId,
      communication_protocol:
        model?.communication_protocol || CommunicationProtocolType.HTTPS,
      current_api_version: model?.current_api_version || null,
      device_type: model?.device_type || 'physical',
      ttl_in_minutes: isCreate ? DEFAULT_TTL_THRESHOLD : model?.ttl_in_minutes,
      user_settings: model?.user_settings || {},

      enable_configuration_device_tab:
        isCreate || model?.enable_configuration_device_tab,
      enable_state_device_tab: isCreate || model?.enable_state_device_tab,
      enable_telemetries_device_tab:
        isCreate || model?.enable_telemetries_device_tab,
      enable_dumps_device_tab: isCreate || model?.enable_dumps_device_tab,
      enable_details_device_tab: isCreate || model?.enable_details_device_tab,
    },
  });

  const [isTtlThresholdActive, setIsTtlThresholdActive] = React.useState(
    form.values.ttl_in_minutes !== null
  );

  const createDeviceModel = useCreateDeviceModel();
  const editDeviceModel = useEditDeviceModel();
  const apiVersions = useDeviceModelApiVersions();

  const isLoading =
    createDeviceModel.isLoading ||
    editDeviceModel.isLoading ||
    apiVersions.isLoading;

  const handleSubmit = async (values: Partial<UseCreateDeviceModelParams>) => {
    const modelData: Partial<UseCreateDeviceModelParams> = {
      ...(model || {}),
      ...values,
    };

    if (!isTtlThresholdActive) {
      modelData.ttl_in_minutes = null;
    }

    if (!modelData.sku) {
      modelData.sku = null;
    }

    try {
      let modelResponse: DeviceModelType;

      if (isCreate) {
        modelResponse = await createDeviceModel.mutateAsync(modelData);
      } else {
        modelResponse = await editDeviceModel.mutateAsync(
          modelData as DeviceModelType
        );
      }

      if (onSuccess) {
        onSuccess(modelResponse);
      } else {
        history.push(`/models/${modelResponse.id}`);
      }

      closeMe();
    } catch (e) {
      console.error(e);
    }
  };

  const hasRegisteredDevices = !model?.devices_count;

  const apiVersionsOptions = apiVersions.isLoading
    ? []
    : map(
        (version) => ({ value: version, label: version }),
        uniq([apiVersions.data.latest, ...apiVersions.data.used])
      );

  if (!form.values.current_api_version && apiVersions.isFetched) {
    const { latest: latestAvailable, used: usedByModels } = apiVersions.data;

    form.setFieldValue(
      'current_api_version',
      usedByModels[0] || latestAvailable
    );
  }

  if (isTtlThresholdActive && form.values.ttl_in_minutes === null) {
    form.setFieldValue('ttl_in_minutes', DEFAULT_TTL_THRESHOLD);
  }

  return (
    <Modal
      opened
      onClose={closeMe}
      size="xl"
      title={
        <Text data-testid="configure-model-modal-title">
          {isCreate ? 'Create New Model' : 'Edit Model'}
        </Text>
      }
    >
      <LoadingOverlay visible={isLoading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing="xl" py="md">
          <Grid>
            <Grid.Col span={1}>
              <Input.Wrapper label="Icon">
                <IconPicker<DeviceModelIconName>
                  icons={DEVICE_MODEL_ICONS}
                  position="bottom"
                  triggerSize={36}
                  iconName={form.values.user_settings?.icon || 'Server'}
                  onChange={(iconName) =>
                    form.setFieldValue('user_settings.icon', iconName)
                  }
                />
              </Input.Wrapper>
            </Grid.Col>
            <Grid.Col span="auto">
              <TextInput
                required
                label="Model name"
                data-testid="model-name-input"
                size="sm"
                data-autofocus
                maxLength={60}
                {...form.getInputProps('name')}
              />
            </Grid.Col>
            <Grid.Col span="auto">
              <TextInput
                label="SKU (optional)"
                data-testid="model-sku-input"
                size="sm"
                {...form.getInputProps('sku')}
              />
            </Grid.Col>
          </Grid>

          <Divider variant="dashed" />

          {hasRegisteredDevices ? (
            <SimpleGrid cols={2}>
              <Select
                label="Authentication method"
                required
                rightSection={
                  <Tooltip
                    label="See documentation"
                    position="right"
                    withinPortal={false}
                  >
                    <Box
                      sx={(theme) => ({
                        cursor: 'pointer',
                        color: theme.colors.gray[6],
                      })}
                      onClick={() =>
                        window.open(
                          'https://dev.xyte.io/reference/set-authentication-method',
                          '_blank'
                        )
                      }
                    >
                      <QuestionCircle />
                    </Box>
                  </Tooltip>
                }
                withinPortal={false}
                data={[
                  { value: AuthMethodType.CloudId, label: 'Cloud ID' },
                  {
                    value: AuthMethodType.MacSN,
                    label: 'MAC & Serial Number',
                  },
                  {
                    value: AuthMethodType.PubKey,
                    label: 'Pub Key',
                    disabled: true,
                  },
                  {
                    value: AuthMethodType.x509,
                    label: 'X.509 Certificate',
                    disabled: true,
                  },
                ]}
                {...form.getInputProps('auth_method')}
              />

              <Select
                label="Communication protocol"
                withinPortal={false}
                required
                data={[
                  {
                    value: CommunicationProtocolType.HTTPS,
                    label: 'HTTPS',
                  },
                  {
                    value: CommunicationProtocolType.MQTT,
                    label: 'MQTT (Experimental)',
                  },
                  {
                    value: CommunicationProtocolType.VPRO,
                    label: 'vPro (Coming Soon)',
                    disabled: true,
                  },
                ]}
                {...form.getInputProps('communication_protocol')}
              />
            </SimpleGrid>
          ) : null}

          <SimpleGrid cols={2}>
            {isCreate ? (
              <Stack>
                <Select
                  label="Device type"
                  rightSection={
                    <Tooltip
                      label="See documentation"
                      position="right"
                      withinPortal={false}
                    >
                      <Box
                        sx={(theme) => ({
                          cursor: 'pointer',
                          color: theme.colors.gray[6],
                        })}
                        onClick={() =>
                          window.open(
                            'https://dev.xyte.io/reference/device-types',
                            '_blank'
                          )
                        }
                      >
                        <QuestionCircle />
                      </Box>
                    </Tooltip>
                  }
                  withinPortal={false}
                  required
                  data={[
                    { value: 'physical', label: 'Physical' },
                    { value: 'digital', label: 'Digital' },
                    { value: 'application', label: 'Application' },
                  ]}
                  {...form.getInputProps('device_type')}
                />
              </Stack>
            ) : null}

            <Select
              label="XYTE API Version"
              disabled={
                apiVersionsOptions?.length === 1 || model?.active === true
              }
              withinPortal={false}
              required
              data={apiVersionsOptions}
              rightSection={
                <Tooltip
                  label="We advise to select the latest API version that your device firmware supports"
                  multiline
                  width={300}
                  position="top"
                >
                  <Box
                    sx={(theme) => ({
                      cursor: 'pointer',
                      color: theme.colors.gray[6],
                    })}
                  >
                    <QuestionCircle />
                  </Box>
                </Tooltip>
              }
              {...form.getInputProps('current_api_version')}
            />
          </SimpleGrid>

          <Input.Wrapper
            label={
              <Group>
                <Text>Offline detection threshold</Text>

                <Switch
                  checked={isTtlThresholdActive}
                  onChange={(event) =>
                    setIsTtlThresholdActive(event.target.checked)
                  }
                />
              </Group>
            }
            description={
              <Text mb="sm">
                When no data is sent from the device for this period (in
                minutes) the device will be marked as "offline"
              </Text>
            }
          >
            <Group spacing="xs">
              <NumberInput
                disabled={!isTtlThresholdActive}
                w="100px"
                min={3}
                required={isTtlThresholdActive}
                {...form.getInputProps('ttl_in_minutes')}
              />

              <Text color={isTtlThresholdActive ? 'gray.7' : 'gray.4'}>
                minutes
              </Text>
            </Group>
          </Input.Wrapper>

          <Divider variant="dashed" />

          <Input.Wrapper
            label={<Text mb="xs">Enabled tabs in device view</Text>}
          >
            <Stack align="start">
              <Switch
                classNames={switchStyles.classes}
                size="xs"
                label="Configuration"
                {...form.getInputProps('enable_configuration_device_tab', {
                  type: 'checkbox',
                })}
              />

              <Switch
                classNames={switchStyles.classes}
                size="xs"
                label="State"
                {...form.getInputProps('enable_state_device_tab', {
                  type: 'checkbox',
                })}
              />

              <Switch
                classNames={switchStyles.classes}
                size="xs"
                label="Telemetries"
                {...form.getInputProps('enable_telemetries_device_tab', {
                  type: 'checkbox',
                })}
              />

              <Switch
                classNames={switchStyles.classes}
                size="xs"
                label="Dumps"
                {...form.getInputProps('enable_dumps_device_tab', {
                  type: 'checkbox',
                })}
              />

              <Switch
                classNames={switchStyles.classes}
                size="xs"
                label="Details"
                {...form.getInputProps('enable_details_device_tab', {
                  type: 'checkbox',
                })}
              />
            </Stack>
          </Input.Wrapper>

          {isCreate ? (
            <>
              <Divider variant="dashed" />

              <Input.Wrapper
                label={
                  <Group align="center" spacing="xs" mb="xs">
                    <Text>Suggested commands</Text>

                    <Tooltip
                      label={
                        <Text>
                          These pre-defined suggested commands are a collection
                          of commonly used actions for devices. By selecting the
                          checkboxes, you can automatically add them to your
                          device model
                        </Text>
                      }
                      multiline
                      width={300}
                      position="right"
                    >
                      <Box color="gray.6">
                        <QuestionCircle />
                      </Box>
                    </Tooltip>
                  </Group>
                }
              >
                <Flex gap="xl">
                  <Checkbox
                    label="Reboot"
                    {...form.getInputProps('create_reboot_command', {
                      type: 'checkbox',
                    })}
                  />

                  <Checkbox
                    label="Upgrade Firmware"
                    {...form.getInputProps('create_firmware_upgrade_command', {
                      type: 'checkbox',
                    })}
                  />

                  <Checkbox
                    label="Request Dump"
                    {...form.getInputProps('create_dump_command', {
                      type: 'checkbox',
                    })}
                  />
                </Flex>
              </Input.Wrapper>
            </>
          ) : null}

          <Divider />
        </Stack>

        <Group position="right" pt="xl">
          <Button
            variant="outline"
            data-testid="configure-model-modal-close-button"
            onClick={closeMe}
          >
            Close
          </Button>

          <Button
            color="primary"
            type="submit"
            disabled={!form.values.name || isLoading}
            data-testid="configure-model-modal-submit-button"
          >
            {isCreate ? 'Create new model' : 'Save changes'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

const useSwitchStyles = createStyles((theme) => ({
  body: {
    display: 'grid',
    flexDirection: 'row-reverse',
    gridTemplateColumns: '100px max-content',
  },
  labelWrapper: {
    order: 1,
  },
  label: {
    paddingLeft: 0,
  },
  track: {
    order: 2,
  },
}));
